import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/game';
import { getSoundSystem } from '../systems/SoundSystem';
import { getMusicPlayer } from '../systems/MusicPlayer';
import { BackHandler, pushSceneState } from '../systems/BackButton';

const SWIPE_THRESHOLD = 50;
const PAGE_COUNT = 7;
const DOT_SPACING = 24;

export class StoryScene extends Phaser.Scene implements BackHandler {
  private currentPage = 0;
  private pageContainer!: Phaser.GameObjects.Container;
  private dots: Phaser.GameObjects.Arc[] = [];
  private pointerStartX = 0;
  private pointerDown = false;
  private leftArrow!: Phaser.GameObjects.Text;
  private rightArrow!: Phaser.GameObjects.Text;

  constructor() {
    super('StoryScene');
  }

  handleBack(): void {
    this.scene.start('TitleScene');
  }

  init(): void {
    this.currentPage = 0;
    this.pointerDown = false;
  }

  preload(): void {
    const icons = ['volcano', 'spirit-dryad', 'spirit-shade', 'spirit-luna', 'spirit-lumina', 'spirit-gnome', 'spirit-undine', 'spirit-sylph'];
    for (const key of icons) {
      if (!this.textures.exists(key)) {
        this.load.image(key, `assets/icons/${key}.png`);
      }
    }
    const tiles = [
      'ground-wetland', 'ground-forest', 'ground-alpine', 'ground-coral', 'ground-meadow',
      'moss-wetland', 'mushroom-forest', 'flower-alpine', 'shell-coral', 'vine-savanna',
    ];
    for (const key of tiles) {
      if (!this.textures.exists(key)) {
        this.load.image(key, `assets/tiles/${key}.png`);
      }
    }
  }

  create(): void {
    pushSceneState();
    getMusicPlayer().startMenu();

    const centerX = GAME_CONFIG.width / 2;

    const closeBtn = this.add.text(GAME_CONFIG.width - 28, 30, '✕', {
      fontSize: '28px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();

    closeBtn.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.scene.start('TitleScene');
    });

    this.add.text(centerX, 40, 'Story', {
      fontSize: '24px',
      color: '#a8e6cf',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.pageContainer = this.add.container(0, 0);
    this.showPage(0);

    this.createDots();
    this.createArrows();
    this.setupSwipe();
  }

  private createDots(): void {
    const centerX = GAME_CONFIG.width / 2;
    const dotsY = GAME_CONFIG.height - 60;
    const totalWidth = (PAGE_COUNT - 1) * DOT_SPACING;
    const startX = centerX - totalWidth / 2;

    for (let i = 0; i < PAGE_COUNT; i++) {
      const dot = this.add.circle(startX + i * DOT_SPACING, dotsY, 6, 0xffffff);
      dot.setAlpha(i === 0 ? 1 : 0.3);
      this.dots.push(dot);
    }
  }

  private createArrows(): void {
    const arrowY = GAME_CONFIG.height / 2;

    this.leftArrow = this.add.text(20, arrowY, '‹', {
      fontSize: '48px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();
    this.leftArrow.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.prevPage();
    });

    this.rightArrow = this.add.text(GAME_CONFIG.width - 20, arrowY, '›', {
      fontSize: '48px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();
    this.rightArrow.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.nextPage();
    });

    this.updateArrows();
  }

  private setupSwipe(): void {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.pointerDown = true;
      this.pointerStartX = pointer.x;
    });

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (!this.pointerDown) return;
      this.pointerDown = false;
      const dx = pointer.x - this.pointerStartX;
      if (dx < -SWIPE_THRESHOLD) {
        this.nextPage();
      } else if (dx > SWIPE_THRESHOLD) {
        this.prevPage();
      }
    });
  }

  private nextPage(): void {
    if (this.currentPage < PAGE_COUNT - 1) {
      this.currentPage++;
      this.showPage(this.currentPage);
      this.updateDots();
      this.updateArrows();
    }
  }

  private prevPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.showPage(this.currentPage);
      this.updateDots();
      this.updateArrows();
    }
  }

  private updateDots(): void {
    for (let i = 0; i < this.dots.length; i++) {
      this.dots[i].setAlpha(i === this.currentPage ? 1 : 0.3);
    }
  }

  private updateArrows(): void {
    this.leftArrow.setAlpha(this.currentPage === 0 ? 0.2 : 1);
    this.rightArrow.setAlpha(this.currentPage === PAGE_COUNT - 1 ? 0.2 : 1);
  }

  private showPage(index: number): void {
    this.pageContainer.removeAll(true);

    switch (index) {
      case 0: this.buildPageAwakening(); break;
      case 1: this.buildPageSpirits(); break;
      case 2: this.buildPageWetlands(); break;
      case 3: this.buildPageForest(); break;
      case 4: this.buildPageAlpine(); break;
      case 5: this.buildPageSavanna(); break;
      case 6: this.buildPageCoral(); break;
    }
  }

  private addStoryText(y: number, text: string): Phaser.GameObjects.Text {
    const leftMargin = (GAME_CONFIG.width - 320) / 2;
    const obj = this.add.text(leftMargin, y, text, {
      fontSize: '15px',
      color: '#cccccc',
      fontFamily: 'sans-serif',
      align: 'left',
      wordWrap: { width: 320 },
      lineSpacing: 6,
    }).setOrigin(0, 0);
    this.pageContainer.add(obj);
    return obj;
  }

  private addPageTitle(text: string, color = '#a8e6cf'): void {
    const centerX = GAME_CONFIG.width / 2;
    const title = this.add.text(centerX, 80, text, {
      fontSize: '20px',
      color,
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    this.pageContainer.add(title);
  }

  private addBiomeTile(groundKey: string, floraKey: string): void {
    const centerX = GAME_CONFIG.width / 2;
    const tileY = 160;
    const tileSize = 72;

    const ground = this.add.image(centerX, tileY, groundKey);
    ground.setDisplaySize(tileSize, tileSize);
    this.pageContainer.add(ground);

    const flora = this.add.image(centerX, tileY, floraKey);
    flora.setDisplaySize(tileSize - 12, tileSize - 12);
    this.pageContainer.add(flora);

    const baseScale = flora.scaleX;
    this.tweens.add({
      targets: flora,
      scaleX: { from: baseScale, to: baseScale * 1.05 },
      scaleY: { from: baseScale, to: baseScale * 1.05 },
      duration: 1200,
      yoyo: true,
      repeat: -1,
    });
  }

  private buildPageAwakening(): void {
    const centerX = GAME_CONFIG.width / 2;
    this.addPageTitle('The Awakening');

    const volcano = this.add.image(centerX, 160, 'volcano');
    volcano.setDisplaySize(72, 72);
    this.pageContainer.add(volcano);

    const baseScale = volcano.scaleX;
    this.tweens.add({
      targets: volcano,
      scaleX: { from: baseScale, to: baseScale * 1.05 },
      scaleY: { from: baseScale, to: baseScale * 1.05 },
      duration: 1200,
      yoyo: true,
      repeat: -1,
    });

    this.addStoryText(220,
      'Once, the world breathed in quiet rhythms. Canopies ' +
      'rustled, tides lapped softly, and mountaintops ' +
      'hummed with stillness. Every living thing held its ' +
      'place in a fragile balance.\n\n' +
      'Then the Great Volcano stirred. Not with fire alone, ' +
      'but with a creeping blight that seeped across the ' +
      'land. Rivers slowed to a trickle. Green faded to ' +
      'grey. The soil forgot how to grow.\n\n' +
      'But the earth remembers what it once was. ' +
      'And somewhere beneath the ash, seeds are waiting.'
    );
  }

  private buildPageSpirits(): void {
    this.addPageTitle('The Spirits');

    this.addStoryText(120,
      'Seven spirits watch over this world. They cannot ' +
      'heal the land alone, but they lend their strength ' +
      'to those who try.'
    );

    const icons = [
      { key: 'spirit-dryad', name: 'Dryad', color: '#6bcb77', desc: 'harmony of growth' },
      { key: 'spirit-shade', name: 'Shade', color: '#9b59b6', desc: 'patience of deep roots' },
      { key: 'spirit-luna', name: 'Luna', color: '#7ec8e3', desc: 'rhythm of the night sky' },
      { key: 'spirit-lumina', name: 'Lumina', color: '#fff0c0', desc: 'shield of quiet light' },
    ];

    const startY = 220;
    const rowH = 48;
    const iconX = 70;
    const textX = 102;

    for (let i = 0; i < icons.length; i++) {
      const y = startY + i * rowH;
      const spirit = icons[i];

      const icon = this.add.image(iconX, y, spirit.key);
      icon.setDisplaySize(40, 40);
      this.pageContainer.add(icon);

      const label = this.add.text(textX, y, `${spirit.name}`, {
        fontSize: '15px',
        color: spirit.color,
        fontFamily: 'sans-serif',
      }).setOrigin(0, 0.5);
      this.pageContainer.add(label);

      const desc = this.add.text(textX + label.width + 8, y, spirit.desc, {
        fontSize: '13px',
        color: '#999999',
        fontFamily: 'sans-serif',
      }).setOrigin(0, 0.5);
      this.pageContainer.add(desc);
    }

    const moreSpirits = [
      { key: 'spirit-gnome', name: 'Gnome', color: '#c8a86b', desc: 'offers seeds to those who listen' },
      { key: 'spirit-undine', name: 'Undine', color: '#5bb8d4', desc: 'sends waves to wash the blight away' },
      { key: 'spirit-sylph', name: 'Sylph', color: '#e8d88c', desc: 'calls forth the Sacred Spring' },
    ];

    const startY2 = startY + icons.length * rowH;

    for (let i = 0; i < moreSpirits.length; i++) {
      const y = startY2 + i * rowH;
      const spirit = moreSpirits[i];

      const icon = this.add.image(iconX, y, spirit.key);
      icon.setDisplaySize(40, 40);
      this.pageContainer.add(icon);

      const label = this.add.text(textX, y, `${spirit.name}`, {
        fontSize: '15px',
        color: spirit.color,
        fontFamily: 'sans-serif',
      }).setOrigin(0, 0.5);
      this.pageContainer.add(label);

      const desc = this.add.text(textX + label.width + 8, y, spirit.desc, {
        fontSize: '13px',
        color: '#999999',
        fontFamily: 'sans-serif',
      }).setOrigin(0, 0.5);
      this.pageContainer.add(desc);
    }
  }

  private buildPageWetlands(): void {
    this.addPageTitle('The Wetlands', '#7ec8e3');
    this.addBiomeTile('ground-wetland', 'moss-wetland');

    this.addStoryText(220,
      'The ash fell first upon the marshes, choking the ' +
      'slow waters and silting the reeds. What was once a ' +
      'gentle maze of streams became a grey and silent mire.\n\n' +
      'But wetlands are patient places. Lay the moss and ' +
      'fern back into the soil, piece by piece, and the ' +
      'water will remember how to flow.\n\n' +
      'Frogs will return to the shallows. ' +
      'Wading birds will come home.'
    );
  }

  private buildPageForest(): void {
    this.addPageTitle('The Forest', '#6bcb77');
    this.addBiomeTile('ground-forest', 'mushroom-forest');

    this.addStoryText(220,
      'The great woods stand quiet now, their leaves ' +
      'curled and dry under a haze of sulphur. No ' +
      'birdsong. No rustle of creatures in the undergrowth.\n\n' +
      'Dryad whispers to the sleeping seeds beneath ' +
      'the forest floor. Place the mushrooms and moss ' +
      'back among the roots, and watch the canopy unfurl.\n\n' +
      'Cool shadows will return, and with them, life.'
    );
  }

  private buildPageAlpine(): void {
    this.addPageTitle('The Alpine', '#d4e5f7');
    this.addBiomeTile('ground-alpine', 'flower-alpine');

    this.addStoryText(220,
      'High above, the mountain air burns where it ' +
      'should bite cold. Snow melts to dark mud, and ' +
      'the peaks lose their gleam under the volcano\'s haze.\n\n' +
      'The alpine flowers are small but stubborn. ' +
      'Return them to the rocky soil and the frost will ' +
      'follow. Clean, cold water will run down to the ' +
      'valleys below, carrying life with it.'
    );
  }

  private buildPageSavanna(): void {
    this.addPageTitle('The Savanna', '#d4a843');
    this.addBiomeTile('ground-meadow', 'vine-savanna');

    this.addStoryText(220,
      'The golden grasses have gone brittle and brown. ' +
      'The wide sky feels heavy, and the earth cracks ' +
      'under a sun that gives no warmth, only glare.\n\n' +
      'Shade stretches across the plains, offering rest ' +
      'from the heat. Lay down vine and flower, and wait ' +
      'for the breeze.\n\n' +
      'When it comes, the grass will ripple gold again, ' +
      'and the cracked earth will drink.'
    );
  }

  private buildPageCoral(): void {
    this.addPageTitle('The Coral', '#e87da0');
    this.addBiomeTile('ground-coral', 'shell-coral');

    this.addStoryText(220,
      'Even the deep places feel the volcano\'s reach. ' +
      'The reefs fade to white as the water warms, and ' +
      'the colourful multitudes retreat into the dark.\n\n' +
      'This is the last and hardest place to heal. ' +
      'Shell by shell, the reef can be rebuilt. And when ' +
      'the balance returns, colour will bloom beneath ' +
      'the waves once more.\n\n' +
      'The ocean will dance again.'
    );
  }
}
